export const catalogueArticles = [
    {
        id: 'catalogue.overview',
        title: 'Catalogue Overview',
        category: 'Catalogue',
        order: 1,
        summary: 'Managing your master price catalogue',
        content: `
The Catalogue is your master database of items with predefined pricing, units, and descriptions. It serves as the source for adding items to job BOQs.

**Key Features:**

- Create and maintain catalogue items
- Set multiple price levels
- Organize items by cost centre
- Track price history
- Create composite assemblies

**Who can do what:** anyone except a Viewer edits items, prices, assemblies, units and cost centres (viewers see the catalogue read-only). Bulk delete, deleting a unit or cost centre, and running an import need the owner, an admin or a manager. Deleting an item is refused while a bill or an assembly uses it — archive it instead.
    `,
        expandableSections: [
            {
                title: 'Catalogue Structure',
                content: `
Each catalogue item contains:

- **Price Code** - Unique identifier
- **Description** - Item description
- **Unit** - Unit of measure
- **Price Levels 1-5** - Different prices for different purposes
- **Cost Centre** - Default cost centre assignment
- **Supplier** - Default supplier (optional)
        `,
            },
            {
                title: 'Cost Centre Panel',
                content: `
The left panel organizes items by cost centre:

- Click a cost centre to filter items
- Use "All Items" to see everything
- Drag items to change their cost centre
- Create new cost centres from the panel
        `,
            },
        ],
        keywords: ['catalogue', 'catalog', 'price list', 'master list', 'items'],
        route: '/catalogue',
        relatedArticles: ['catalogue.add-item', 'catalogue.prices', 'boq.add-item'],
    },
    {
        id: 'catalogue.add-item',
        title: 'Adding Catalogue Items',
        category: 'Catalogue',
        order: 2,
        summary: 'How to create new items in the catalogue',
        content: `
Adding items to the catalogue creates reusable entries that can be added to any job's BOQ.

**Required Fields:**

- **Price Code** - Unique identifier (e.g., "CONC-030")
- **Description** - Clear item description
- **Unit** - Unit of measure
- **Price Level 1** - At least one price required
    `,
        expandableSections: [
            {
                title: 'Creating an Item',
                content: `
1. Click **Add Item** in the toolbar
2. Enter the required fields:
   - Price Code (must be unique)
   - Description
   - Select a Unit
3. Set pricing:
   - Level 1 (Estimating)
   - Additional levels as needed
4. Optional settings:
   - Default Cost Centre
   - Default Supplier
   - Keywords for search
5. Click **Save**
        `,
            },
            {
                title: 'Price Code Conventions',
                content: `
Recommended price code formats:

- **Category prefix** - CONC (concrete), TIMB (timber), ELEC (electrical)
- **Sequential number** - CONC-001, CONC-002
- **Descriptive** - CONC-SLAB-100, CONC-FOOTING-300

Keep codes:
- Unique and consistent
- Easy to search
- Meaningful to users
        `,
            },
        ],
        keywords: ['add item', 'create item', 'new catalogue item', 'price code'],
        route: '/catalogue',
        relatedArticles: ['catalogue.overview', 'catalogue.prices'],
    },
    {
        id: 'catalogue.prices',
        title: 'Managing Prices',
        category: 'Catalogue',
        order: 3,
        summary: 'Understanding and managing catalogue price levels',
        content: `
The catalogue supports multiple price levels and keeps a dated history of every price for auditing and historical quotes. The **current** price at a level is the latest entry whose effective date is today or earlier — a price dated next month waits until then.
**Price Levels:**

| Level | Typical Use |
|-------|-------------|
| 1 | Estimating (default) |
| 2 | Regional pricing |
| 3 | Sales/quoted |
| 4 | Custom |
| 5 | Custom |
    
**Panel tips:** the item panel that slides up from the bottom can be resized — drag its top edge (double-click to reset; the height is remembered). The Supplier Prices tab shows one compact card per supplier with the current price, and the price history below.
`,
        expandableSections: [
            {
                title: 'Setting Prices',
                content: `
**Individual Item:**
1. Open the item for editing
2. Enter prices for each level (1–5; never negative)
3. Save the item — each level you changed gets a new entry dated today

**Dated prices:** the assistant's *Add price* tool and the API accept an effective date (YYYY-MM-DD), so a supplier's price list that starts next month can be loaded now and becomes current on that day.

**Bulk Price Change:**
1. Click **Bulk Price Change** in toolbar
2. Select items to update
3. Choose update method:
   - Percentage increase/decrease
   - Fixed amount change
   - Set specific price
4. Preview changes
5. Apply — each changed price is a new entry dated today (or the "valid from" date you chose); the previous price stays in the history
`,
            },
            {
                title: 'Price History',
                content: `
Every price write — the item form, Bulk Price Change, the quick reprice and copy-level actions in Catalogue Management, imports and the assistant — adds or updates the entry for that level **on its effective date**. Writing twice on the same day updates that day's entry; an earlier day's entry is never changed. Each entry records who made it and why.

With **Settings → Company → Write price log** switched on, every change is also appended to the price audit log with the old and new price and the reason.

View history by clicking the price history icon on an item.
`,
            },
            {
                title: 'Supplier Prices',
                content: `
In addition to catalogue prices, items can have supplier-specific prices:

- Set in the Suppliers module
- Override catalogue prices when assigned
- Tracked per supplier per item
- Date-effective

See **Suppliers > Prices** for managing supplier pricing.
        `,
            },
        ],
        keywords: ['prices', 'pricing', 'price levels', 'cost', 'rate'],
        route: '/catalogue',
        relatedArticles: ['catalogue.overview', 'catalogue.bulk-price'],
        screenshots: [
            {
                src: 'catalogue/catalogue-price-levels.png',
                alt: 'Catalogue price levels',
                caption: 'Manage multiple price levels for each catalogue item',
            },
        ],
    },
    {
        id: 'catalogue.bulk-price',
        title: 'Bulk Price Changes',
        category: 'Catalogue',
        order: 4,
        summary: 'How to update prices for multiple items at once',
        content: `
The Bulk Price Change feature allows you to update prices for multiple catalogue items in a single operation.

**Update Methods:**

- **Percentage** - Increase or decrease by a percentage
- **Fixed Amount** - Add or subtract a fixed value
- **Set Value** - Set all selected items to a specific price
    `,
        expandableSections: [
            {
                title: 'Bulk Update Process',
                content: `
1. Select items in the catalogue grid
   - Use Ctrl+Click for multiple selections
   - Or use filters to show items to update
2. Click **Bulk Price Change**
3. Configure the update:
   - Which price level(s) to update
   - Update method (%, fixed, set)
   - Amount/value
4. Preview the changes
5. Review items that will be affected
6. Click **Apply** to confirm

**What is written:** a new price entry per item and level, dated today or the "valid from" date, with the estimator's name and notes. Previous entries stay as history. The starting price is the current one — a price dated in the future is ignored and left alone.

**Tip:** Use the preview to verify changes before applying.
`,
            },
            {
                title: 'Selective Updates',
                content: `
You can target specific items:

- **By Cost Centre** - Filter to a cost centre first
- **By Selection** - Select specific items
- **By Search** - Use the search to filter items
- **All Items** - Apply to entire catalogue (use with caution)
        `,
            },
        ],
        keywords: ['bulk price', 'price update', 'mass update', 'price change'],
        route: '/catalogue',
        relatedArticles: ['catalogue.prices', 'catalogue.overview'],
        screenshots: [
            {
                src: 'catalogue/catalogue-bulk-price.png',
                alt: 'Bulk Price Change modal',
                caption: 'Update prices for multiple catalogue items at once',
            },
        ],
    },
    {
        id: 'catalogue.recipes',
        title: 'Composite Assemblies',
        category: 'Catalogue',
        order: 5,
        summary: 'Creating composite items from multiple catalogue items',
        content: `
Assemblies allow you to create composite items that include multiple catalogue items. When added to a BOQ, all component items are included automatically.

**Use Cases:**

- Pre-configured assemblies
- Standard packages
- Trade packages with multiple components
    `,
        expandableSections: [
            {
                title: 'Component formulas & variables',
                content: `
A component's quantity can be a **formula** instead of a fixed number — edit it in the Assembly Editor's Formula column (the pencil opens the **Formula Builder**).

Formulas use square-bracket tokens, the same style as zzTakeoff: \`[Qty]\` is the BOQ line quantity, and named variables like \`[Length]\`, \`[Width]\`, \`[Height]\` and \`[Perimeter]\` are measurements in **metres** (a unit suffix like \`[Perimeter:m]\` is accepted and ignored). Tokens display as bubbles in the Formula Builder — double-click a bubble to select it. Any \`[Name]\` you use — including ones you invent, like \`[Layers]\` — automatically becomes a question the estimator is asked when the assembly is added to a BOQ.

The result is a **per-unit** quantity (it is multiplied by the line quantity), which gives two useful patterns:
- **Once per line:** \`12 / [Qty]\` — exactly 12, no matter the line quantity.
- **Perimeter-driven:** \`([Perimeter] * 1.05) / [Qty]\` — the whole perimeter (plus waste) once per line, e.g. edge formwork.
        `,
            },
            {
                title: 'Creating an Assembly',
                content: `
1. Click **Assembly Editor** in the toolbar
2. Or edit an existing item and select "Is Assembly"
3. Add component items:
   - Search and add catalogue items
   - Set quantities for each component
4. Save the assembly

When added to a BOQ, all components are added with their proportional quantities.
        `,
            },
            {
                title: 'Assembly Pricing',
                content: `
Assembly prices can be calculated:

- **Component Sum** - Total of all component prices
- **Override** - Set a fixed assembly price

The assembly's unit price can be different from the component sum for margin or package pricing.
        `,
            },
        ],
        keywords: ['recipe', 'composite', 'assembly', 'package', 'bundle'],
        route: '/catalogue',
        relatedArticles: ['catalogue.overview', 'catalogue.add-item'],
    },
    {
        id: 'catalogue.units',
        title: 'Units of Measure',
        category: 'Catalogue',
        order: 6,
        summary: 'Understanding units and their calculation routines',
        content: `
Units of measure define how quantities are measured and how prices are calculated. Each unit has a calculation routine that determines the pricing formula.

**Common Units:**

| Unit | Description | Routine |
|------|-------------|---------|
| EA | Each | Standard |
| M | Linear Metre | Linear |
| M2 | Square Metre | Area |
| M3 | Cubic Metre | Standard |
| ITEM | Lump Sum | Price Only |
    `,
        expandableSections: [
            {
                title: 'Unit Properties',
                content: `
Each unit has:

- **Code** - Short identifier (EA, M, M2)
- **Description** - Full name
- **Calculation Routine** - How to calculate value
- **Divisor** - Conversion factor
- **Rounding** - Quantity rounding rules

See **Features > Calculation Routines** for detailed formula information.
        `,
            },
            {
                title: 'Managing Units',
                content: `
Units are managed in Settings:

1. Go to **Settings > Units**
2. View existing units
3. Add new units with:
   - Code and description
   - Calculation routine
   - Divisor and rounding

**Note:** Changing a unit's routine, divisor or rounding changes every bill line total that uses that unit, on every job. A unit can be deleted only when no catalogue item and no bill line uses it; codes must be unique.
`,
            },
        ],
        keywords: ['units', 'measure', 'calculation', 'routine', 'divisor'],
        route: '/catalogue',
        relatedArticles: ['features.calculations', 'settings.units'],
    },
    {
        id: 'catalogue.import',
        title: 'Importing Catalogue Data',
        category: 'Catalogue',
        order: 7,
        summary: 'How to import items from external files',
        content: `
Import catalogue items from Excel or CSV files to quickly build or update your catalogue.

**Import Options:**

- **New Items** - Add items that don't exist
- **Update Existing** - Update prices for existing items
- **Full Replace** - Replace all data for matching items
    `,
        expandableSections: [
            {
                title: 'Import Process',
                content: `
1. Click **Import** in the toolbar
2. Select your file (Excel or CSV)
3. Map columns to catalogue fields:
   - Price Code (required)
   - Description
   - Unit
   - Prices
4. Choose import mode:
   - Add new only
   - Update existing only
   - Both
5. Preview the import
6. Confirm to import
        `,
            },
            {
                title: 'File Format',
                content: `
Recommended columns:

| Column | Required | Description |
|--------|----------|-------------|
| Price Code | Yes | Unique identifier |
| Description | Yes | Item description |
| Unit | Yes | Unit code (must exist — an unknown unit is refused, the item is not created without one) |
| Price 1 | Yes | Level 1 price |
| Price 2-5 | No | Other level prices |
| Cost Centre | No | Default cost centre |
        `,
            },
        ],
        keywords: ['import', 'upload', 'excel', 'csv', 'bulk add'],
        route: '/catalogue',
        relatedArticles: ['catalogue.overview', 'catalogue.add-item'],
    },
    {
        id: 'catalogue.cost-centres',
        title: 'Catalogue Cost Centres',
        category: 'Catalogue',
        order: 8,
        summary: 'Organizing catalogue items by cost centre',
        content: `
Cost centres in the catalogue provide default organization for items. When items are added to a BOQ, they inherit their catalogue cost centre if no other is specified.

**Benefits:**

- Pre-organized items for BOQ
- Consistent categorization
- Quick filtering and searching
    `,
        expandableSections: [
            {
                title: 'Assigning Cost Centres',
                content: `
**When Creating Items:**
- Select a cost centre before creating
- Or choose in the item form

**Changing Assignments:**
- Edit the item and change cost centre
- Or drag items in the grid to cost centres in the panel

**Bulk Assignment:**
1. Select multiple items
2. Right-click > Assign Cost Centre
3. Choose the target
        `,
            },
        ],
        keywords: ['cost centre', 'cost center', 'organize', 'category'],
        route: '/catalogue',
        relatedArticles: ['catalogue.overview', 'boq.cost-centres'],
    },
];
